import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';

import firebase from '../../Firebase/Firebase';
import Loader from '../../Components/UI/Loader/Loader';
import { Container } from '../../Hoc/elements';
import { useI18n } from '../../i18n/I18nProvider';

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WORKDAY_HOURS = 8;

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const startOfWeek = (date) => {
  const next = startOfDay(date);
  const day = next.getDay() || 7;
  next.setDate(next.getDate() - day + 1);
  return next;
};

const dateKey = (date) => date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';

const sameDay = (left, right) => left && right && dateKey(left) === dateKey(right);

const itemAnchor = (item) => toDate(item.start_at) || toDate(item.due_at) || toDate(item.sort_at);
const itemEnd = (item) => toDate(item.end_at) || (itemAnchor(item) ? new Date(itemAnchor(item).getTime() + 60 * 60 * 1000) : null);
const itemUpdatedAt = (item) => toDate(item.updated_at || item.created_at)?.getTime() || 0;
const isOpen = (item) => !['completed', 'cancelled', 'archived'].includes(item.status);

const radians = (degrees) => degrees * Math.PI / 180;
const distanceKm = (left, right) => {
  const lat1 = Number(left?.latitude ?? left?.lat);
  const lon1 = Number(left?.longitude ?? left?.lng);
  const lat2 = Number(right?.latitude ?? right?.lat);
  const lon2 = Number(right?.longitude ?? right?.lng);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  const dLat = radians(lat2 - lat1);
  const dLon = radians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const formatTime = (date, locale) => date
  ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)
  : '';

const priorityRank = { high: 0, normal: 1, low: 2 };

const nextReminderTimestamp = (value) => {
  const target = value instanceof Date ? value.getTime() : toDate(value)?.getTime();
  if (!(target > Date.now())) return null;
  for (const offset of [DAY_MS, 60 * 60 * 1000]) {
    const reminderAt = target - offset;
    if (reminderAt > Date.now()) return firebase.firestore.Timestamp.fromMillis(reminderAt);
  }
  return firebase.firestore.Timestamp.fromMillis(Date.now());
};

const paymentLabel = (state, t) => {
  if (state === 'deposit_paid') return { text: t('planning.paymentReady'), tone: 'success' };
  if (state === 'deposit_pending') return { text: t('planning.paymentWaiting'), tone: 'warning' };
  if (state === 'final_pending') return { text: t('planning.finalWaiting'), tone: 'pending' };
  if (state === 'paid') return { text: t('planning.paid'), tone: 'success' };
  return null;
};

const TaskCard = ({ item, language, locale, t, onToggle }) => {
  const anchor = itemAnchor(item);
  const end = itemEnd(item);
  const payment = paymentLabel(item.payment_state, t);
  const routeAddress = item.location?.address;
  const routeUrl = routeAddress
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(routeAddress)}`
    : '';
  const completed = item.status === 'completed';
  const waiting = item.status === 'waiting';
  const displayTitle = language === 'en' ? (item.title_en || item.title) : (item.title_fr || item.title);
  const displayNotes = language === 'en' ? (item.notes_en || item.notes) : (item.notes_fr || item.notes);

  return (
    <Card $completed={completed} $priority={item.priority || 'normal'}>
      <CardTop>
        <TypeIcon $type={item.type}>{item.type === 'payment' ? <PaymentsOutlinedIcon /> : item.type === 'site_visit' || item.type === 'work' ? <CalendarMonthOutlinedIcon /> : <TaskAltOutlinedIcon />}</TypeIcon>
        <CardTitle>
          <strong>{displayTitle}</strong>
          <MetaLine>
            {anchor ? <span><TodayOutlinedIcon /> {new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(anchor)}{formatTime(anchor, locale) ? ` · ${formatTime(anchor, locale)}` : ''}{end && anchor && end.getTime() !== anchor.getTime() ? `–${formatTime(end, locale)}` : ''}</span> : <span>{t('planning.unscheduled')}</span>}
            {item.location?.city && <span><LocationOnOutlinedIcon /> {item.location.city}</span>}
          </MetaLine>
        </CardTitle>
        {waiting ? (
          <WaitingBadge>{language === 'en' ? 'Waiting · no action' : 'En attente · aucune action'}</WaitingBadge>
        ) : (
          <StatusButton type="button" $completed={completed} onClick={() => onToggle(item)}>
            <CheckCircleOutlineOutlinedIcon />
            <span>{completed ? t('planning.reopen') : t('planning.complete')}</span>
          </StatusButton>
        )}
      </CardTop>

      {displayNotes && <Notes>{displayNotes}</Notes>}
      {payment && <PaymentBadge $tone={payment.tone}><PaymentsOutlinedIcon /> {payment.text}</PaymentBadge>}

      <CardFooter>
        <Source>{item.auto_generated ? t('planning.auto') : t('planning.manual')}{item.deadline_kind === 'recommended' ? ` · ${t('planning.recommended')}` : ''}</Source>
        <Actions>
          {routeUrl && <ExternalAction href={routeUrl} target="_blank" rel="noreferrer"><RouteOutlinedIcon /> {t('planning.route')}</ExternalAction>}
          {item.demand_id && <ProjectAction to="/jobs"><OpenInNewOutlinedIcon /> {t('planning.openProject')}</ProjectAction>}
        </Actions>
      </CardFooter>
    </Card>
  );
};

const ArtisanAgenda = ({ artisanId }) => {
  const { language, locale, t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('today');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({ title: '', dueAt: '', startAt: '', endAt: '', priority: 'normal', notes: '' });
  const now = new Date();
  const todayStart = startOfDay(now);
  const weekStart = startOfWeek(now);
  const weekEnd = new Date(weekStart.getTime() + 7 * DAY_MS);

  useEffect(() => {
    if (!artisanId || typeof window === 'undefined') return;
    const key = `lpb-agenda-refresh-v2-${artisanId}`;
    const lastRefresh = Number(window.sessionStorage.getItem(key) || 0);
    if (Date.now() - lastRefresh < 15 * 60 * 1000) return;
    window.sessionStorage.setItem(key, String(Date.now()));
    firebase.app().functions('europe-west1').httpsCallable('refreshMyAgenda')({})
      .catch((refreshError) => console.warn('Agenda refresh failed.', refreshError));
  }, [artisanId]);

  useEffect(() => {
    if (!artisanId) return undefined;
    setLoading(true);
    return firebase.firestore().collection('users').doc(artisanId)
      .collection('agenda_items')
      .orderBy('sort_at', 'asc')
      .limit(300)
      .onSnapshot((snapshot) => {
        setItems(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
        setError('');
      }, (nextError) => {
        console.error(nextError);
        setError(nextError?.message || 'Planning unavailable.');
        setLoading(false);
      });
  }, [artisanId]);

  const effectiveItems = useMemo(() => {
    const manual = [];
    const generatedByProject = new Map();
    items.forEach((item) => {
      const generated = item.auto_generated === true || item.source === 'demand';
      if (!generated || !item.demand_id) {
        manual.push(item);
        return;
      }
      const current = generatedByProject.get(item.demand_id);
      const canonical = item.id === `${item.demand_id}_next_action`;
      const currentCanonical = current?.id === `${item.demand_id}_next_action`;
      if (!current || (canonical && !currentCanonical) || (canonical === currentCanonical && itemUpdatedAt(item) > itemUpdatedAt(current))) {
        generatedByProject.set(item.demand_id, item);
      }
    });
    return [...manual, ...generatedByProject.values()];
  }, [items]);

  const openItems = useMemo(() => effectiveItems.filter(isOpen), [effectiveItems]);
  const actionableItems = useMemo(() => openItems.filter((item) => item.status !== 'waiting'), [openItems]);
  const waitingItems = useMemo(() => openItems.filter((item) => item.status === 'waiting'), [openItems]);
  const todayItems = useMemo(() => actionableItems.filter((item) => {
    const anchor = itemAnchor(item);
    const overdue = anchor && anchor < now && !sameDay(anchor, now);
    return overdue || sameDay(anchor, now);
  }).sort((a, b) => {
    const priority = (priorityRank[a.priority] ?? 1) - (priorityRank[b.priority] ?? 1);
    if (priority !== 0) return priority;
    return (itemAnchor(a)?.getTime() || 0) - (itemAnchor(b)?.getTime() || 0);
  }), [actionableItems, now]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => new Date(weekStart.getTime() + index * DAY_MS)), [weekStart]);
  const weekItems = useMemo(() => actionableItems.filter((item) => {
    const anchor = itemAnchor(item);
    return anchor && anchor >= weekStart && anchor < weekEnd;
  }), [actionableItems, weekStart, weekEnd]);

  const conflicts = useMemo(() => {
    const scheduled = weekItems.filter((item) => item.start_at && item.end_at).sort((a, b) => itemAnchor(a) - itemAnchor(b));
    const pairs = [];
    for (let index = 0; index < scheduled.length; index += 1) {
      for (let next = index + 1; next < scheduled.length; next += 1) {
        if (!sameDay(itemAnchor(scheduled[index]), itemAnchor(scheduled[next]))) break;
        if (itemAnchor(scheduled[next]) < itemEnd(scheduled[index])) pairs.push([scheduled[index].id, scheduled[next].id]);
      }
    }
    return pairs;
  }, [weekItems]);

  const readyProjects = useMemo(() => new Set(actionableItems.filter((item) => item.payment_state === 'deposit_paid').map((item) => item.demand_id).filter(Boolean)).size, [actionableItems]);
  const todayAppointments = todayItems.filter((item) => ['site_visit', 'work', 'appointment'].includes(item.type)).length;
  const overdueCount = actionableItems.filter((item) => {
    const anchor = itemAnchor(item);
    return anchor && anchor < todayStart;
  }).length;

  const dayStats = useMemo(() => weekDays.map((day) => {
    const dayItems = weekItems.filter((item) => sameDay(itemAnchor(item), day));
    const scheduledMinutes = dayItems.reduce((sum, item) => {
      const start = itemAnchor(item);
      const end = itemEnd(item);
      if (!start || !end) return sum + 30;
      return sum + Math.max(15, Math.round((end - start) / 60000));
    }, 0);
    let nearbyPairs = 0;
    dayItems.forEach((item, index) => {
      dayItems.slice(index + 1).forEach((next) => {
        const distance = distanceKm(item.location, next.location);
        if (distance !== null && distance <= 15) nearbyPairs += 1;
      });
    });
    return {
      day,
      items: dayItems,
      workload: Math.min(140, Math.round(scheduledMinutes / (DEFAULT_WORKDAY_HOURS * 60) * 100)),
      nearbyPairs,
    };
  }), [weekDays, weekItems]);

  const toggleItem = async (item) => {
    try {
      const reopening = item.status === 'completed';
      await firebase.firestore().collection('users').doc(artisanId).collection('agenda_items').doc(item.id).update({
        status: reopening ? 'open' : 'completed',
        completed_at: reopening ? null : firebase.firestore.FieldValue.serverTimestamp(),
        sent_reminders: reopening ? [] : (Array.isArray(item.sent_reminders) ? item.sent_reminders : []),
        next_reminder_at: reopening ? nextReminderTimestamp(item.start_at || item.due_at) : null,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (nextError) {
      setError(t('planning.updateError'));
    }
  };

  const saveTask = async () => {
    setFormError('');
    if (!form.title.trim() || !form.dueAt) {
      setFormError(t('planning.validation'));
      return;
    }
    setSaving(true);
    try {
      const ref = firebase.firestore().collection('users').doc(artisanId).collection('agenda_items').doc();
      const due = new Date(form.dueAt);
      const start = form.startAt ? new Date(form.startAt) : null;
      const end = form.endAt ? new Date(form.endAt) : null;
      await ref.set({
        agenda_item_id: ref.id,
        artisan_id: artisanId,
        source: 'manual',
        auto_generated: false,
        type: 'task',
        title: form.title.trim().slice(0, 160),
        notes: form.notes.trim().slice(0, 1000),
        priority: form.priority,
        status: 'open',
        due_at: firebase.firestore.Timestamp.fromDate(due),
        start_at: start && !Number.isNaN(start.getTime()) ? firebase.firestore.Timestamp.fromDate(start) : null,
        end_at: end && !Number.isNaN(end.getTime()) ? firebase.firestore.Timestamp.fromDate(end) : null,
        sort_at: firebase.firestore.Timestamp.fromDate(start && !Number.isNaN(start.getTime()) ? start : due),
        reminder_offsets_minutes: [1440, 60],
        sent_reminders: [],
        next_reminder_at: nextReminderTimestamp(start || due),
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setForm({ title: '', dueAt: '', startAt: '', endAt: '', priority: 'normal', notes: '' });
      setModalOpen(false);
    } catch (nextError) {
      console.error(nextError);
      setFormError(t('planning.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <Container>
        <Hero>
          <HeroCopy>
            <Eyebrow>{t('planning.eyebrow')}</Eyebrow>
            <h1>{t('planning.title')}</h1>
            <p>{t('planning.subtitle')}</p>
          </HeroCopy>
          <AddButton type="button" onClick={() => setModalOpen(true)}><AddTaskOutlinedIcon /> {t('planning.addTask')}</AddButton>
        </Hero>

        <SummaryGrid>
          <SummaryCard><TaskAltOutlinedIcon /><strong>{actionableItems.length}</strong><span>{t('planning.openActions')}</span></SummaryCard>
          <SummaryCard><TodayOutlinedIcon /><strong>{todayAppointments}</strong><span>{t('planning.todayAppointments')}</span></SummaryCard>
          <SummaryCard $success={readyProjects > 0}><PaymentsOutlinedIcon /><strong>{readyProjects}</strong><span>{t('planning.readyProjects')}</span></SummaryCard>
          <SummaryCard $warning={overdueCount > 0}><ErrorOutlineOutlinedIcon /><strong>{overdueCount}</strong><span>{t('planning.overdue')}</span></SummaryCard>
        </SummaryGrid>

        {conflicts.length > 0 && (
          <ConflictNotice><ErrorOutlineOutlinedIcon /><div><strong>{t('planning.conflictTitle')}</strong><span>{t('planning.conflictBody', { count: conflicts.length })}</span></div></ConflictNotice>
        )}

        <Tabs role="tablist">
          <Tab type="button" $active={tab === 'today'} onClick={() => setTab('today')}><TodayOutlinedIcon /> {t('planning.today')}</Tab>
          <Tab type="button" $active={tab === 'week'} onClick={() => setTab('week')}><CalendarMonthOutlinedIcon /> {t('planning.week')}</Tab>
          <Tab type="button" $active={tab === 'tasks'} onClick={() => setTab('tasks')}><TaskAltOutlinedIcon /> {t('planning.tasks')}</Tab>
        </Tabs>

        {loading ? <LoadingWrap><Loader /></LoadingWrap> : error ? <ErrorBox>{error}</ErrorBox> : (
          <Content>
            {tab === 'today' && (
              todayItems.length ? <TaskList>{todayItems.map((item) => <TaskCard key={item.id} item={item} language={language} locale={locale} t={t} onToggle={toggleItem} />)}</TaskList>
                : <EmptyState><EventAvailableOutlinedIcon /><h2>{t('planning.noToday')}</h2><p>{t('planning.noTodayBody')}</p></EmptyState>
            )}

            {tab === 'week' && (
              weekItems.length ? (
                <WeekGrid>
                  {dayStats.map(({ day, items: dayItems, workload, nearbyPairs }) => (
                    <DayColumn key={dateKey(day)} $today={sameDay(day, now)}>
                      <DayHeader>
                        <div><span>{new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(day)}</span><strong>{day.getDate()}</strong></div>
                        <LoadBar><i style={{ width: `${Math.min(100, workload)}%` }} /></LoadBar>
                        <small>{t('planning.workload', { percent: workload })}</small>
                        {nearbyPairs > 0 && <Nearby><LocationOnOutlinedIcon /> {t('planning.nearby', { count: nearbyPairs + 1 })}</Nearby>}
                      </DayHeader>
                      <DayItems>
                        {dayItems.length ? dayItems.map((item) => <CompactCard key={item.id} $priority={item.priority || 'normal'}>
                          <time>{formatTime(itemAnchor(item), locale) || '—'}</time>
                          <strong>{language === 'en' ? (item.title_en || item.title) : (item.title_fr || item.title)}</strong>
                          {paymentLabel(item.payment_state, t) && <span>{paymentLabel(item.payment_state, t).text}</span>}
                        </CompactCard>) : <DayEmpty>—</DayEmpty>}
                      </DayItems>
                    </DayColumn>
                  ))}
                </WeekGrid>
              ) : <EmptyState><CalendarMonthOutlinedIcon /><h2>{t('planning.noWeek')}</h2></EmptyState>
            )}

            {tab === 'tasks' && (
              openItems.length ? (
                <TaskGroups>
                  {actionableItems.length > 0 && (
                    <TaskGroup>
                      <GroupHeading><strong>{language === 'en' ? 'Your next actions' : 'Vos prochaines actions'}</strong><span>{actionableItems.length}</span></GroupHeading>
                      <TaskList>{actionableItems.slice().sort((a, b) => (priorityRank[a.priority] ?? 1) - (priorityRank[b.priority] ?? 1) || (itemAnchor(a)?.getTime() || 0) - (itemAnchor(b)?.getTime() || 0)).map((item) => <TaskCard key={item.id} item={item} language={language} locale={locale} t={t} onToggle={toggleItem} />)}</TaskList>
                    </TaskGroup>
                  )}
                  {waitingItems.length > 0 && (
                    <TaskGroup>
                      <GroupHeading $waiting><strong>{language === 'en' ? 'Waiting on the client or payment' : 'En attente du client ou du paiement'}</strong><span>{waitingItems.length}</span></GroupHeading>
                      <TaskList>{waitingItems.map((item) => <TaskCard key={item.id} item={item} language={language} locale={locale} t={t} onToggle={toggleItem} />)}</TaskList>
                    </TaskGroup>
                  )}
                </TaskGroups>
              ) : <EmptyState><CheckCircleOutlineOutlinedIcon /><h2>{t('planning.noTasks')}</h2></EmptyState>
            )}
          </Content>
        )}
      </Container>

      {modalOpen && (
        <ModalBackdrop role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setModalOpen(false); }}>
          <Modal role="dialog" aria-modal="true" aria-label={t('planning.manualTitle')}>
            <ModalHeader><div><Eyebrow>{t('planning.manual')}</Eyebrow><h2>{t('planning.manualTitle')}</h2></div><Close type="button" onClick={() => setModalOpen(false)}>×</Close></ModalHeader>
            <Field><label htmlFor="agenda-title">{t('planning.taskTitle')}</label><input id="agenda-title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} maxLength={160} placeholder={t('planning.taskTitlePlaceholder')} /></Field>
            <Field><label htmlFor="agenda-due">{t('planning.dueDate')}</label><input id="agenda-due" type="datetime-local" value={form.dueAt} onChange={(event) => setForm({ ...form, dueAt: event.target.value })} /></Field>
            <TwoColumns>
              <Field><label htmlFor="agenda-start">{t('planning.startDate')}</label><input id="agenda-start" type="datetime-local" value={form.startAt} onChange={(event) => setForm({ ...form, startAt: event.target.value })} /></Field>
              <Field><label htmlFor="agenda-end">{t('planning.endDate')}</label><input id="agenda-end" type="datetime-local" value={form.endAt} onChange={(event) => setForm({ ...form, endAt: event.target.value })} /></Field>
            </TwoColumns>
            <Field><label htmlFor="agenda-priority">{t('planning.priority')}</label><select id="agenda-priority" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}><option value="low">{t('planning.low')}</option><option value="normal">{t('planning.normal')}</option><option value="high">{t('planning.high')}</option></select></Field>
            <Field><label htmlFor="agenda-notes">{t('planning.notes')}</label><textarea id="agenda-notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} maxLength={1000} /></Field>
            {formError && <FormError>{formError}</FormError>}
            <ModalActions><Secondary type="button" onClick={() => setModalOpen(false)}>{t('planning.cancel')}</Secondary><Primary type="button" onClick={saveTask} disabled={saving}>{saving ? t('planning.saving') : t('planning.save')}</Primary></ModalActions>
          </Modal>
        </ModalBackdrop>
      )}
    </Page>
  );
};

const Page = styled.main`min-height:calc(100vh - 4.6rem);padding:clamp(1rem,3vw,2.2rem) 0 7rem;background:#f6f4f0;color:#141414;`;
const Hero = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;padding:clamp(1rem,2.8vw,1.5rem);border:1px solid #e4ded5;border-radius:1.2rem;background:linear-gradient(135deg,#fff,#faf6ef);box-shadow:0 .45rem 1.3rem rgba(0,0,0,.035);@media(max-width:42rem){display:grid;}`;
const HeroCopy = styled.div`max-width:47rem;h1{margin:.15rem 0;font-size:clamp(1.65rem,4vw,2.45rem);}p{margin:.35rem 0 0;color:#6c675f;line-height:1.5;}`;
const Eyebrow = styled.span`color:#8b6b38;font-size:.67rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;`;
const AddButton = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.45rem;min-height:2.8rem;padding:.65rem 1rem;border:0;border-radius:999px;background:#111;color:#fff;font:inherit;font-size:.75rem;font-weight:850;cursor:pointer;white-space:nowrap;svg{font-size:1.05rem;}@media(max-width:42rem){width:100%;}`;
const SummaryGrid = styled.section`display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:.7rem;margin:1rem 0;@media(max-width:54rem){grid-template-columns:repeat(2,minmax(0,1fr));}@media(max-width:30rem){grid-template-columns:1fr 1fr;}`;
const SummaryCard = styled.div`display:grid;grid-template-columns:auto 1fr;align-items:center;gap:.12rem .55rem;padding:.85rem;border:1px solid ${({$success,$warning})=>$warning?'#e5c0b9':$success?'#b9d9c5':'#e5dfd6'};border-radius:.95rem;background:${({$success,$warning})=>$warning?'#fff4f1':$success?'#eff9f2':'#fff'};svg{grid-row:1/3;font-size:1.25rem;color:${({$success,$warning})=>$warning?'#a34035':$success?'#287449':'#9a7845'};}strong{font-size:1.15rem;}span{color:#716c65;font-size:.67rem;line-height:1.3;}`;
const ConflictNotice = styled.div`display:flex;align-items:center;gap:.7rem;margin-bottom:1rem;padding:.8rem 1rem;border:1px solid #e3beb7;border-radius:.9rem;background:#fff4f1;color:#7d2e27;svg{flex:0 0 auto;}div{display:grid;gap:.12rem;}strong{font-size:.78rem;}span{font-size:.68rem;}`;
const Tabs = styled.div`display:inline-flex;gap:.25rem;margin-bottom:.8rem;padding:.3rem;border:1px solid #e2dcd3;border-radius:999px;background:#fff;@media(max-width:30rem){display:grid;grid-template-columns:repeat(3,1fr);width:100%;}`;
const Tab = styled.button`display:inline-flex;align-items:center;justify-content:center;gap:.35rem;min-height:2.45rem;padding:.5rem .85rem;border:0;border-radius:999px;background:${({$active})=>$active?'#111':'transparent'};color:${({$active})=>$active?'#fff':'#5f5a53'};font:inherit;font-size:.7rem;font-weight:850;cursor:pointer;svg{font-size:1rem;}`;
const Content = styled.section`min-width:0;`;
const TaskGroups = styled.div`display:grid;gap:1rem;`;
const TaskGroup = styled.section`display:grid;gap:.55rem;`;
const GroupHeading = styled.header`display:flex;align-items:center;justify-content:space-between;gap:.6rem;padding:.2rem .1rem;color:${({$waiting})=>$waiting?'#756d63':'#2c2925'};strong{font-size:.73rem;}span{display:grid;place-items:center;min-width:1.6rem;height:1.6rem;padding:0 .4rem;border-radius:999px;background:${({$waiting})=>$waiting?'#eeeae4':'#111'};color:${({$waiting})=>$waiting?'#5f5850':'#fff'};font-size:.62rem;font-weight:900;}`;
const TaskList = styled.div`display:grid;gap:.7rem;`;
const Card = styled.article`display:grid;gap:.65rem;padding:.9rem;border:1px solid #e2ddd5;border-left:4px solid ${({$priority})=>$priority==='high'?'#b34b40':$priority==='low'?'#a8a198':'#c0a375'};border-radius:1rem;background:${({$completed})=>$completed?'#f2f0ec':'#fff'};box-shadow:0 .35rem 1rem rgba(0,0,0,.03);opacity:${({$completed})=>$completed?.72:1};`;
const CardTop = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:start;gap:.7rem;@media(max-width:38rem){grid-template-columns:auto minmax(0,1fr);}`;
const TypeIcon = styled.span`width:2.4rem;height:2.4rem;display:grid;place-items:center;border-radius:.75rem;background:#171717;color:#d8ba82;svg{font-size:1.15rem;}`;
const CardTitle = styled.div`min-width:0;display:grid;gap:.25rem;strong{font-size:.84rem;line-height:1.35;overflow-wrap:anywhere;}`;
const MetaLine = styled.div`display:flex;flex-wrap:wrap;gap:.25rem .65rem;color:#777168;font-size:.65rem;span{display:inline-flex;align-items:center;gap:.2rem;}svg{font-size:.8rem;}`;
const WaitingBadge = styled.span`display:inline-flex;align-items:center;justify-content:center;min-height:2.2rem;padding:.42rem .65rem;border:1px solid #ded7cd;border-radius:999px;background:#f4f1ec;color:#6f675e;font-size:.62rem;font-weight:850;white-space:nowrap;@media(max-width:38rem){grid-column:1/-1;width:100%;}`;
const StatusButton = styled.button`display:inline-flex;align-items:center;gap:.28rem;min-height:2.2rem;padding:.42rem .65rem;border:1px solid ${({$completed})=>$completed?'#d0cbc3':'#cdb88f'};border-radius:999px;background:${({$completed})=>$completed?'#fff':'#fbf4e8'};color:#4d4334;font:inherit;font-size:.64rem;font-weight:850;cursor:pointer;svg{font-size:.9rem;}@media(max-width:38rem){grid-column:1/-1;width:100%;justify-content:center;}`;
const Notes = styled.p`margin:0;padding:.65rem .75rem;border-radius:.7rem;background:#f7f5f1;color:#615c55;font-size:.7rem;line-height:1.45;`;
const PaymentBadge = styled.div`display:inline-flex;align-items:center;gap:.3rem;justify-self:start;padding:.4rem .6rem;border-radius:999px;background:${({$tone})=>$tone==='success'?'#e8f6ed':$tone==='warning'?'#fff0e4':'#eef1f4'};color:${({$tone})=>$tone==='success'?'#256440':$tone==='warning'?'#814726':'#53606a'};font-size:.65rem;font-weight:850;svg{font-size:.9rem;}`;
const CardFooter = styled.footer`display:flex;align-items:center;justify-content:space-between;gap:.7rem;padding-top:.55rem;border-top:1px solid #eeeae4;@media(max-width:38rem){align-items:flex-start;display:grid;}`;
const Source = styled.span`color:#8a847b;font-size:.6rem;`;
const Actions = styled.div`display:flex;flex-wrap:wrap;gap:.35rem;`;
const ExternalAction = styled.a`display:inline-flex;align-items:center;gap:.25rem;padding:.38rem .55rem;border-radius:999px;background:#f1eee9;color:#3f3a34;text-decoration:none;font-size:.62rem;font-weight:850;svg{font-size:.8rem;}`;
const ProjectAction = styled(Link)`display:inline-flex;align-items:center;gap:.25rem;padding:.38rem .55rem;border-radius:999px;background:#111;color:#fff;text-decoration:none;font-size:.62rem;font-weight:850;svg{font-size:.8rem;}`;
const EmptyState = styled.div`min-height:18rem;display:grid;place-items:center;align-content:center;gap:.35rem;padding:2rem;border:1px dashed #d8d0c5;border-radius:1rem;background:#fff;color:#756e65;text-align:center;svg{font-size:2.2rem;color:#b39769;}h2{margin:.2rem 0 0;color:#222;font-size:1rem;}p{margin:0;font-size:.72rem;}`;
const LoadingWrap = styled.div`min-height:18rem;display:grid;place-items:center;`;
const ErrorBox = styled.p`padding:1rem;border-radius:.8rem;background:#fff0ed;color:#912f27;`;
const WeekGrid = styled.div`display:grid;grid-template-columns:repeat(7,minmax(9rem,1fr));gap:.5rem;overflow-x:auto;padding-bottom:.35rem;scrollbar-width:thin;`;
const DayColumn = styled.section`min-height:22rem;padding:.55rem;border:1px solid ${({$today})=>$today?'#c0a375':'#e4ded5'};border-radius:.9rem;background:${({$today})=>$today?'#fffbf3':'#fff'};`;
const DayHeader = styled.header`display:grid;gap:.25rem;padding:.2rem .2rem .55rem;border-bottom:1px solid #ece7df;>div{display:flex;align-items:center;justify-content:space-between;}span{text-transform:capitalize;color:#716b63;font-size:.68rem;}strong{font-size:1rem;}small{color:#8a837a;font-size:.57rem;}`;
const LoadBar = styled.div`height:.22rem;overflow:hidden;border-radius:999px;background:#eeeae3;i{display:block;height:100%;border-radius:inherit;background:#b28a50;}`;
const Nearby = styled.span`display:flex!important;align-items:center;gap:.15rem;color:#3d6f4d!important;font-size:.57rem!important;svg{font-size:.72rem;}`;
const DayItems = styled.div`display:grid;gap:.4rem;padding-top:.5rem;`;
const CompactCard = styled.div`display:grid;gap:.12rem;padding:.5rem;border-left:3px solid ${({$priority})=>$priority==='high'?'#b44b40':'#c0a375'};border-radius:.55rem;background:#f7f4ef;time{color:#8b6b38;font-size:.58rem;font-weight:900;}strong{font-size:.66rem;line-height:1.3;}span{color:#6c655c;font-size:.55rem;line-height:1.25;}`;
const DayEmpty = styled.span`padding:.6rem;color:#bbb4aa;text-align:center;`;
const ModalBackdrop = styled.div`position:fixed;inset:0;z-index:1600;display:grid;place-items:center;padding:1rem;background:rgba(0,0,0,.55);backdrop-filter:blur(5px);`;
const Modal = styled.div`width:min(96vw,36rem);max-height:calc(100dvh - 2rem);overflow:auto;padding:1rem;border-radius:1.1rem;background:#fff;box-shadow:0 1.5rem 4rem rgba(0,0,0,.28);`;
const ModalHeader = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1rem;h2{margin:.15rem 0 0;}`;
const Close = styled.button`width:2.4rem;height:2.4rem;border:0;border-radius:50%;background:#f1eee9;font-size:1.5rem;cursor:pointer;`;
const Field = styled.div`display:grid;gap:.35rem;margin-bottom:.75rem;label{font-size:.72rem;font-weight:850;}input,textarea,select{width:100%;box-sizing:border-box;padding:.72rem;border:1px solid #dcd5ca;border-radius:.72rem;background:#fff;color:#111;font:inherit;}textarea{min-height:6rem;resize:vertical;}`;
const TwoColumns = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.7rem;@media(max-width:32rem){grid-template-columns:1fr;}`;
const FormError = styled.p`margin:.4rem 0;color:#9d342b;font-size:.72rem;`;
const ModalActions = styled.div`display:flex;justify-content:flex-end;gap:.55rem;margin-top:1rem;@media(max-width:30rem){display:grid;grid-template-columns:1fr;}`;
const Secondary = styled.button`min-height:2.65rem;padding:.6rem .9rem;border:1px solid #ded7cd;border-radius:999px;background:#fff;font:inherit;font-weight:850;cursor:pointer;`;
const Primary = styled(Secondary)`border-color:#111;background:#111;color:#fff;`;

const mapStateToProps = ({ firebase: firebaseState }) => ({ artisanId: firebaseState.auth?.uid || '' });
export default connect(mapStateToProps)(ArtisanAgenda);
